import { useActionState, useEffect, useState } from "react"
import { Plus, Sparkles, X } from "lucide-react"
import { ListingImportProgress } from "@/components/deals/ListingImportProgress"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useListingImport } from "@/hooks/use-listing-import"

const INITIAL_VALUES = {
  address: "",
  sourceLink: "",
  isHotLead: false,
}

const CREATE_STATES = {
  editing: "editing",
  creating: "creating",
  importing: "importing",
  waitingChoice: "waitingChoice",
}

const TERMINAL_IMPORT_STATUSES = new Set(["succeeded", "partial", "failed"])

export function AddDealDialog({ isOpen, onOpenChange, onAddDeal, onOpenDeal, csrfToken }) {
  const [values, setValues] = useState(INITIAL_VALUES)
  const [createState, setCreateState] = useState(CREATE_STATES.editing)
  const [createdDeal, setCreatedDeal] = useState(null)

  const resetDialog = () => {
    setValues(INITIAL_VALUES)
    setCreateState(CREATE_STATES.editing)
    setCreatedDeal(null)
  }

  const closeDialog = () => {
    resetDialog()
    onOpenChange(false)
  }

  const [submitError, submitAction, isPending] = useActionState(async (_previousError, formData) => {
    const submittedValues = {
      address: String(formData.get("address") ?? ""),
      sourceLink: String(formData.get("sourceLink") ?? ""),
      isHotLead: formData.get("isHotLead") === "true",
    }
    if (!submittedValues.address.trim()) return "Property address is required"

    try {
      const deal = await onAddDeal(submittedValues)
      if (!submittedValues.sourceLink.trim()) {
        closeDialog()
        return ""
      }

      setCreatedDeal(deal)
      setCreateState(CREATE_STATES.importing)
      return ""
    } catch (error) {
      return error.message
    }
  }, "")

  const importActive = createState === CREATE_STATES.importing
  const { listingImport, elapsedMs, hasExceededWait, error: pollingError } = useListingImport({
    dealId: createdDeal?.id,
    initialImport: createdDeal?.listingImport,
    enabled: isOpen && importActive,
    csrfToken,
  })
  const baseViewState = isPending ? CREATE_STATES.creating : createState
  const viewState = baseViewState === CREATE_STATES.importing && hasExceededWait
    ? CREATE_STATES.waitingChoice
    : baseViewState
  const isImporting = [CREATE_STATES.importing, CREATE_STATES.waitingChoice].includes(viewState)

  const openCreatedDeal = (tab) => {
    const dealId = createdDeal?.id
    closeDialog()
    if (dealId) onOpenDeal?.(dealId, tab)
  }

  useEffect(() => {
    if (!isImporting || !TERMINAL_IMPORT_STATUSES.has(listingImport?.status)) return

    const dealId = createdDeal?.id
    onOpenChange(false)
    if (dealId) onOpenDeal?.(dealId, "enrichment")
  }, [createdDeal?.id, isImporting, listingImport?.status, onOpenChange, onOpenDeal])

  const updateValue = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }))
  }

  const handleOpenChange = (open) => {
    if (!open) resetDialog()
    onOpenChange(open)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        overlayClassName="bg-background/80 backdrop-blur-sm"
        className="max-w-md gap-0 overflow-hidden rounded-2xl bg-card p-0 shadow-xl sm:max-w-md"
        showCloseButton={false}
      >
        <div className="flex items-center justify-between border-b border-border bg-muted/20 p-4">
          <DialogTitle className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground">
            {isImporting ? <Sparkles className="h-4 w-4 text-primary" /> : <Plus className="h-4 w-4 text-primary" />}
            {isImporting ? "Blueprint Import" : "Add Deal"}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              className="p-1 text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Close Add Deal"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogClose>
        </div>
        <DialogDescription className="sr-only">
          Add a property to the acquisition pipeline for location and evidence analysis.
        </DialogDescription>

        {isImporting ? (
          <ListingImportProgress
            elapsedMs={elapsedMs}
            hasExceededWait={viewState === CREATE_STATES.waitingChoice}
            hasPollingError={Boolean(pollingError)}
            onOpenDeal={() => openCreatedDeal("overview")}
            onStayOnPipeline={closeDialog}
          />
        ) : (
          <form className="space-y-4 p-6" action={submitAction}>
            <div className="space-y-1.5">
              <Label
                htmlFor="deal-address"
                className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
              >
                Property Address
              </Label>
              <Input
                id="deal-address"
                name="address"
                value={values.address}
                placeholder="e.g. 12 Chester Road, Chester, CH1 2AB"
                className="rounded-lg bg-background px-3 py-2 text-sm font-medium placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary"
                aria-invalid={submitError === "Property address is required"}
                aria-describedby={submitError === "Property address is required" ? "deal-address-error" : undefined}
                onChange={(event) => updateValue("address", event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Include the full postcode where available so Sentinel can resolve the location and collect local evidence.
              </p>
              {submitError === "Property address is required" ? (
                <p id="deal-address-error" className="text-sm font-medium text-destructive">
                  {submitError}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="deal-source-link"
                className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70"
              >
                Source Link (Optional)
              </Label>
              <Input
                id="deal-source-link"
                name="sourceLink"
                value={values.sourceLink}
                type="url"
                placeholder="Paste listing link (Rightmove, LoopNet, Agent Website)"
                className="rounded-lg bg-background px-3 py-2 text-sm font-medium placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary"
                onChange={(event) => updateValue("sourceLink", event.target.value)}
              />
            </div>

            <div className="flex items-center justify-between py-1">
              <div className="space-y-0.5">
                <Label
                  id="deal-hot-lead-label"
                  htmlFor="deal-hot-lead"
                  className="text-sm font-bold text-foreground"
                >
                  Mark as Hot Lead
                </Label>
                <p
                  id="deal-hot-lead-description"
                  className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/80"
                >
                  Prioritise this deal for immediate attention
                </p>
              </div>
              <Switch
                id="deal-hot-lead"
                size="figma"
                checked={values.isHotLead}
                onCheckedChange={(checked) => updateValue("isHotLead", Boolean(checked))}
                aria-labelledby="deal-hot-lead-label"
                aria-describedby="deal-hot-lead-description"
                className="h-5 w-9 cursor-pointer justify-center shadow-none data-[state=unchecked]:bg-muted"
                thumbClassName="size-3.5 data-[state=checked]:translate-x-1.5 data-[state=unchecked]:-translate-x-1.5"
              />
              <input type="hidden" name="isHotLead" value={String(values.isHotLead)} />
            </div>

            {submitError && submitError !== "Property address is required" ? (
              <p className="text-sm font-medium text-destructive">
                {submitError}
              </p>
            ) : null}

            <div className="pt-2">
              <Button
                type="submit"
                disabled={isPending}
                className="h-auto w-full rounded-lg py-3 text-xs font-bold uppercase tracking-widest shadow-sm"
              >
                {isPending ? "Adding Deal..." : "Add Deal to Pipeline"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
